import React, {
  Dispatch,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { get } from 'lodash';
import { useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { useSelector } from 'react-redux';

import { AuthStoreState } from '@futrli/auth';
import {
  invalidateLiveReports,
  invalidatePerformanceDashboards
} from '@futrli/views/mutations/invalidateListingQueries';
import { LiveReportDetail, LiveReportList } from '@futrli-sdk/predict';
import { Logger } from '@futrli/shared-services';
import { PerformanceDashboard } from '@futrli-sdk/performance_dashboards';

import {
  addToPerformanceDashboards,
  fetchPerformanceDashboards,
  removeFromPerformanceDashboards
} from '../core/api/reporting/performance-dashboards';
import { createContext } from '../core/context';
import {
  createNewLiveReport,
  fetchLiveReportById,
  fetchLiveReports,
  LiveReportPayload,
  patchLiveReport
} from '../core/api/reporting/report-builder';
import { fetchMembersInOrganisation } from '../core/api/members';
import { fetchRoles } from '../core/api/roles';
import { PageSetup, ReportMappingData } from './types';
import { PredictManagementContext } from '../predict-management/context/predict-management-context';
import { usePredictionsInfoContext } from '../forecast/detail/context/predictions-info-context';

interface Props {
  children: ReactNode;
  isInDashboardPage?: boolean;
}
interface ReportsContextProps {
  reports: LiveReportList[];
  fetchReportById: (id: string, options?: { rethrowOnError?: boolean }) => Promise<LiveReportDetail | undefined>;
  createReport: (
    name: string,
    PageSetup: PageSetup,
    templateId?: string | undefined,
    mappingData?: ReportMappingData,
    locale?: string,
    pageCount?: number
  ) => Promise<LiveReportDetail | undefined>;
  patchReport: (reportId: string, data: LiveReportPayload) => Promise<void>;
  reportViewRef: MutableRefObject<HTMLDivElement | null>;
  handlePrint: () => void;
  reportPendingPrint: string | undefined;
  setReportPendingPrint: Dispatch<SetStateAction<string | undefined>>;
  isPrinting: boolean;
  isGuest: boolean;
  getPerformanceDashboards: () => Promise<void>;
  addPerformanceDashboard: (id: string, name: string, order?: number | null) => Promise<void>;
  performanceDashboards: PerformanceDashboard[];
  isProcessingPerformanceDashboards: boolean;
  removePerformanceDashboard: (id: string) => Promise<void>;
}

const ReportsContext = createContext<ReportsContextProps>();

function useContextSetup(isDashboard: boolean) {
  const { addErrorMessage } = useContext(PredictManagementContext);
  const { organisationId: tempOrganisationId, workspaceId: tempWorkspaceId } = useParams();
  const organisationId = tempOrganisationId as string;
  const workspaceId = tempWorkspaceId as string;
  const { isConsolidation } = usePredictionsInfoContext();

  const [reports, setReports] = useState<LiveReportList[]>([]);
  const [performanceDashboards, setPerformanceDashboards] = useState<PerformanceDashboard[]>([]);
  const [isFetchingReports, setIsFetchingReports] = useState(false);
  const [isProcessingPerformanceDashboards, setIsProcessingPerformanceDashboards] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isCreatingReport, setIsCreatingReport] = useState(false);
  const [isPatchingReport, setIsPatchingReport] = useState(false);
  const [isGuest, setIsGuest] = useState(false);
  const [reportPendingPrint, setReportPendingPrint] = useState<string | undefined>();
  const currentUserId = useSelector((state: AuthStoreState) =>
    get(state, 'authReducers.authenticatedUser.attributes.sub', '')
  );

  const reportViewRef = useRef<HTMLDivElement | null>(null);

  // Fetch API data
  useEffect(() => {
    if (!isDashboard) {
      fetchReports();
    }
  }, [workspaceId, organisationId, isDashboard]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchData = async () => {
      const [fetchedRoles, fetchedUsers] = await Promise.all([
        fetchRoles(workspaceId),
        fetchMembersInOrganisation(workspaceId, organisationId, isConsolidation)
      ]);
      const user = (fetchedUsers || []).find(u => u.id === currentUserId);
      const guestRole = (fetchedRoles || []).find(r => r.name.toLowerCase() === 'guest');
      setIsGuest(!!(user && guestRole && user.role.id === guestRole.id));
    };
    if (!isDashboard) {
      fetchData();
    }
  }, [workspaceId, organisationId, currentUserId, isDashboard, isConsolidation]);

  const fetchReports = useCallback(async () => {
    try {
      if (!isFetchingReports) {
        setIsFetchingReports(true);
        const reports = await fetchLiveReports(workspaceId, organisationId, isConsolidation);
        setReports(reports);
      }
    } catch (e) {
      addErrorMessage?.(new Error('Failure to fetch Live Reports'));
      Logger.error(e as string);
    } finally {
      setIsFetchingReports(false);
    }
  }, [addErrorMessage, isConsolidation, isFetchingReports, organisationId, workspaceId]);

  const fetchReportById = async (id: string, options?: { rethrowOnError?: boolean }) => {
    if (isFetchingReports) {
      return undefined;
    }
    try {
      setIsFetchingReports(true);
      const report = await fetchLiveReportById(workspaceId, organisationId, id, isConsolidation);
      return report;
    } catch (e) {
      addErrorMessage?.(new Error('Failure to fetch Live Report by id'));
      Logger.error(e as string);
      if (options?.rethrowOnError) {
        throw e;
      }
    } finally {
      setIsFetchingReports(false);
    }
  };

  const createReport = async (
    name: string,
    pageSetup: PageSetup,
    templateId?: string | undefined,
    mappingData?: ReportMappingData,
    locale?: string,
    pageCount?: number
  ) => {
    try {
      if (!isCreatingReport) {
        setIsCreatingReport(true);
        const reportObject: {
          name: string;
          locale?: string;
          data: {
            pageSetup: PageSetup;
            sourceTemplateId?: string;
            userTimezone: string;
            data_reference_mapping?: ReportMappingData;
          };
          orientation: 'portrait' | 'landscape' | 'dashboard';
          page_count: number;
        } = {
          name: name || 'untitled',
          locale: !templateId ? locale : undefined,
          data: {
            pageSetup,
            userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          },
          orientation: pageSetup.orientation,
          page_count: pageCount ?? 1
        };
        if (templateId) {
          reportObject.data.sourceTemplateId = templateId;
        }
        if (mappingData) {
          reportObject.data.data_reference_mapping = mappingData;
        }

        const createdReport = await createNewLiveReport(workspaceId, organisationId, reportObject, isConsolidation);
        setIsCreatingReport(false);
        return createdReport;
      }
    } catch (e) {
      addErrorMessage?.(new Error('Failure to create new Live Report'));
      Logger.error(e as string);
    }
  };

  const patchReport = async (reportId: string, data: LiveReportPayload) => {
    try {
      if (!isPatchingReport) {
        setIsPatchingReport(true);
        await patchLiveReport(workspaceId, organisationId, reportId, data, isConsolidation);
        invalidateLiveReports(organisationId, workspaceId, !!isConsolidation);
      }
    } catch (e) {
      addErrorMessage?.(new Error('Failure to patch Report'));
      Logger.error(e as string);
    } finally {
      setIsPatchingReport(false);
    }
  };

  const getPerformanceDashboards = async () => {
    try {
      if (!isProcessingPerformanceDashboards) {
        setIsProcessingPerformanceDashboards(true);

        const perDashs = await fetchPerformanceDashboards(workspaceId, organisationId);
        setPerformanceDashboards(perDashs);
      }
    } catch (e) {
      addErrorMessage?.(new Error('Failure to fetch Performance Dashboards'));
      Logger.error(e as string);
    } finally {
      setIsProcessingPerformanceDashboards(false);
    }
  };

  const addPerformanceDashboard = async (dashboardId: string, dashboardName: string, order?: number | null) => {
    try {
      if (!isProcessingPerformanceDashboards) {
        setIsProcessingPerformanceDashboards(true);

        const newDash = await addToPerformanceDashboards(
          workspaceId,
          organisationId,
          dashboardId,
          dashboardName,
          order
        );
        setPerformanceDashboards([...performanceDashboards, newDash]);
        invalidatePerformanceDashboards(organisationId, workspaceId);
      }
    } catch (e) {
      addErrorMessage?.(new Error('Failure to Add to Performance Dashboards'));
      Logger.error(e as string);
    } finally {
      setIsProcessingPerformanceDashboards(false);
    }
  };

  const removePerformanceDashboard = async (dashboardId: string) => {
    try {
      if (!isProcessingPerformanceDashboards) {
        setIsProcessingPerformanceDashboards(true);

        await removeFromPerformanceDashboards(workspaceId, organisationId, dashboardId);
        setPerformanceDashboards(performanceDashboards.filter(pd => pd.report_id === dashboardId));
        invalidatePerformanceDashboards(organisationId, workspaceId);
      }
    } catch (e) {
      addErrorMessage?.(new Error('Failure to Remove from Performance Dashboards'));
      Logger.error(e as string);
    } finally {
      setIsProcessingPerformanceDashboards(false);
    }
  };

  const onBeforeGetContent = async () => {
    setIsPrinting(true);
  };

  const onAfterPrint = async () => {
    setIsPrinting(false);
  };

  const handlePrint = useReactToPrint({
    content: () => reportViewRef.current,
    onBeforeGetContent,
    onAfterPrint
  });

  return {
    reports,
    fetchReportById,
    createReport,
    patchReport,
    reportViewRef,
    handlePrint,
    reportPendingPrint,
    setReportPendingPrint,
    isPrinting,
    isGuest,
    getPerformanceDashboards,
    addPerformanceDashboard,
    removePerformanceDashboard,
    performanceDashboards,
    isProcessingPerformanceDashboards
  };
}

export function ReportsContextProvider({ children, isInDashboardPage }: Props) {
  const value = useContextSetup(!!isInDashboardPage);

  return <ReportsContext.Provider value={value}>{children}</ReportsContext.Provider>;
}

export const useReportsContext = ReportsContext.useContext;
